import {Injectable} from '@angular/core';
import {HttpParams} from '@angular/common/http';

import {ASeColonne, ASeOnglet} from './DonneesRecherche';
import {ValAppSettingsService} from '../../../../val-ammon-services/val-app-settings-lib/src/public-api';
import {ValApiResultat, ValBaseService} from '../../../../val-ammon-services/val-services-communs-lib/src/public-api';
import {ASeDefinitionFiltre, ASeDefinitionTri, ASeExecuterRechercheParametres} from './ASeExecuterRecherche';
import {ASeMettreAJourOrdreColonnesUtilisateurParametres} from './ASeMettreAJourOrdreColonnesUtilisateur';
import {ASeMettreAJourTriColonnesUtilisateurParametres} from './ASeMettreAJourTriColonnesUtilisateur';
import {ASeMettreAJourVisibiliteColonnesUtilisateurParametres} from './ASeMettreAJourVisibiliteColonnesUtilisateur';
import {ASeMettreAJourTailleColonneUtilisateurParametres} from './ASeMettreAJourTailleColonneUtilisateur';
import {ASeMettreAJourFiltreColonnesUtilisateurCorps, ASeMettreAJourFiltreColonnesUtilisateurParametres} from './ASeMettreAJourFiltreColonnesUtilisateur';
import {ASeMettreAJourGroupeColonnesUtilisateurParametres} from './ASeMettreAJourGroupeColonnesUtilisateur';
import {ASeInformationsRecherche, ASeRetournerInformationsRechercheParametres} from './ASeRetournerInformationsRecherche';
import {ASeExecuterRequeteBandeauParametres} from './ASeExecuterRequeteBandeau';
import {ASeExporterGrilleVersExcelParametres} from './ASeExporterGrilleVersExcel';

//Service recherche
@Injectable (
{
  providedIn: 'root'
})
export class RechercheService extends ValBaseService 
{
  //Création
  //  Paramètres
  //    appSettingsService : Service d’accès à la configuration de l’application
  constructor (private appSettingsService: ValAppSettingsService) 
  {
    super ();
  }

  //Url de base
  protected override get BaseUrl () : string 
  {
    return `${this.appSettingsService.Configuration.UrlApi.Search}/v1.0/recherche/`;
  }

  //Retourne les informations de la recherche
  //  Paramètres
  //    aseRetournerInformationsRechercheParametres : Paramètres de récupération des informations de la recherche
  //  Retourne
  //    Résultat de la récupération des informations de la recherche
  public async RetournerInformationsRecherche (aseRetournerInformationsRechercheParametres: ASeRetournerInformationsRechercheParametres) : Promise<ValApiResultat<ASeInformationsRecherche>> 
  {
    let operation: Promise<ValApiResultat<ASeInformationsRecherche>>;
    let parametres: HttpParams;

    parametres = new HttpParams ();

    parametres = parametres.set ("Identifiant", aseRetournerInformationsRechercheParametres.identifiant);

    operation = this.ExecuterRequeteGet<ASeInformationsRecherche> ("information", parametres);

    return operation;
  }  

  //Retourne les onglets de recherche
  //  Paramètres
  //    identifiantRecherche : Identifiant de la recherche
  //  Retourne
  //    Résultat de la récupération des onglets de recherche
  public async RetournerOngletsRecherche (identifiantRecherche: string) : Promise<ValApiResultat<ASeOnglet[]>> 
  {
    let operation: Promise<ValApiResultat<ASeOnglet[]>>;
    let parametres: HttpParams;

    parametres = new HttpParams (
    {
      fromObject:
      {
        IdentifiantRecherche: identifiantRecherche
      }
    });

    operation = this.ExecuterRequeteGet<ASeOnglet[]> ("onglets", parametres);

    return operation;
  }

  //Retourne les colonnes d’un onglet
  //  Paramètres
  //    identifiantOnglet : Identifiant de l’onglet
  //  Retourne
  //    Résultat de la récupération des colonnes de l’onglet
  public async RetournerColonnesOnglet (identifiantOnglet: string) : Promise<ValApiResultat<ASeColonne[]>> 
  {
    let operation: Promise<ValApiResultat<ASeColonne[]>>;
    let parametres: HttpParams;

    parametres = new HttpParams (
    {
      fromObject:
      {
        IdentifiantOnglet: identifiantOnglet
      }
    });

    operation = this.ExecuterRequeteGet<ASeColonne[]> ("colonnes", parametres);

    return operation;
  }

  //Exécute la recherche
  //  Paramètres
  //    aseExecuterRechercheParametres : Paramètres d’exécution d’une requête de recherche
  //  Retourne
  //    Résultat de l’exécution de la recherche
  public async ExecuterRecherche (aseExecuterRechercheParametres: ASeExecuterRechercheParametres) : Promise<ValApiResultat<any>> 
  {
    let operation: Promise<ValApiResultat<any>>;
    let parametres: HttpParams;

    parametres = new HttpParams ();

    parametres = parametres.set ("Domaine", aseExecuterRechercheParametres.domaine);
    parametres = parametres.set ("IdentifiantOnglet", aseExecuterRechercheParametres.identifiantOnglet);
    parametres = parametres.set ("NumeroPage", aseExecuterRechercheParametres.numeroPage);

    parametres = this.AjouterDefinitionsTrisParametres (aseExecuterRechercheParametres.definitionsTris, parametres);
    parametres = this.AjouterDefinitionsFiltresParametres (aseExecuterRechercheParametres.definitionsFiltres, parametres);

    operation = this.ExecuterRequeteGet<any> ("execute", parametres);

    return operation;
  }

  //Met à jour de l’ordre des colonnes de l’utilisateur
  //  Paramètres
  //    aseMettreAJourOrdreColonnesUtilisateurParametres : Paramètres de mise à jour de l’ordre des colonnes de l’utilisateur
  //  Retourne
  //    Résultat de la mise à jour de l’ordre des colonnes de l’utilisateur
  public async MettreAJourOrdreColonnesUtilisateur (aseMettreAJourOrdreColonnesUtilisateurParametres: ASeMettreAJourOrdreColonnesUtilisateurParametres) : Promise<ValApiResultat<any>> 
  {
    let operation: Promise<ValApiResultat<any>>;

    operation = this.ExecuterRequetePost<any> ("columns/order/update", aseMettreAJourOrdreColonnesUtilisateurParametres);

    return operation;
  }

  //Met à jour le tri des colonnes de l’utilisateur
  //  Paramètres
  //    aseMettreAJourTriColonnesUtilisateurParametres : Paramètres de mise à jour du tri des colonnes de l’utilisateur
  //  Retourne
  //    Résultat de la mise à jour du tri des colonnes de l’utilisateur
  public async MettreAJourTriColonnesUtilisateur (aseMettreAJourTriColonnesUtilisateurParametres: ASeMettreAJourTriColonnesUtilisateurParametres) : Promise<ValApiResultat<any>> 
  {
    let operation: Promise<ValApiResultat<any>>;

    operation = this.ExecuterRequetePost<any> ("columns/sort/update", aseMettreAJourTriColonnesUtilisateurParametres);

    return operation;
  }

  //Met à jour la visibilité des colonnes de l’utilisateur
  //  Paramètres
  //    aseMettreAJourVisibiliteColonnesUtilisateurParametres : Paramètres de mise à jour de la visibilité des colonnes de l’utilisateur
  //  Retourne
  //    Résultat de la mise à jour de la visibilité des colonnes de l’utilisateur
  public async MettreAJourVisibiliteColonnesUtilisateur (aseMettreAJourVisibiliteColonnesUtilisateurParametres: ASeMettreAJourVisibiliteColonnesUtilisateurParametres) : Promise<ValApiResultat<any>> 
  {
    let operation: Promise<ValApiResultat<any>>;

    operation = this.ExecuterRequetePost<any> ("columns/visibility/update", aseMettreAJourVisibiliteColonnesUtilisateurParametres);

    return operation;
  }

  //Met à jour la taille d’une colonne de l’utilisateur
  //  Paramètres
  //    aseMettreAJourTailleColonneUtilisateurParametres : Paramètres de mise à jour de la taille d’une colonne de l’utilisateur
  //  Retourne
  //    Résultat de la mise à jour de la taille d’une colonne de l’utilisateur
  public async MettreAJourTailleColonneUtilisateur (aseMettreAJourTailleColonneUtilisateurParametres: ASeMettreAJourTailleColonneUtilisateurParametres) : Promise<ValApiResultat<any>> 
  {
    let operation: Promise<ValApiResultat<any>>;

    operation = this.ExecuterRequetePost<any> ("column/size/update", aseMettreAJourTailleColonneUtilisateurParametres);

    return operation;
  }

  //Met à jour le filtre des colonnes de l’utilisateur
  //  Paramètres
  //    aseMettreAJourFiltreColonnesUtilisateurParametres : Paramètres de mise à jour du filtre des colonnes de l’utilisateur
  //  Retourne
  //    Résultat de la mise à jour du filtre des colonnes de l’utilisateur
  public async MettreAJourFiltreColonnesUtilisateur (aseMettreAJourFiltreColonnesUtilisateurParametres: ASeMettreAJourFiltreColonnesUtilisateurParametres) : Promise<ValApiResultat<any>> 
  {
    let operation: Promise<ValApiResultat<any>>;
    let corps: ASeMettreAJourFiltreColonnesUtilisateurCorps;

    //La date part comme à l’exécution : le jour choisi, pas l’instant UTC que produirait JSON
    corps =
    {
      ...aseMettreAJourFiltreColonnesUtilisateurParametres,
      filtreColonnes: aseMettreAJourFiltreColonnesUtilisateurParametres.filtreColonnes.map ((fcl) =>
      {
        return {...fcl, valeurFiltreDate: fcl.valeurFiltreDate ? this.FormaterDateFiltre (fcl.valeurFiltreDate) : null};
      })
    };

    operation = this.ExecuterRequetePost<any> ("columns/filter/update", corps);

    return operation;
  }

  //Met à jour le groupe des colonnes de l’utilisateur
  //  Paramètres
  //    aseMettreAJourGroupeColonnesUtilisateurParametres : Paramètres de mise à jour du groupe des colonnes de l’utilisateur
  //  Retourne
  //    Résultat de la mise à jour du groupe des colonnes de l’utilisateur
  public async MettreAJourGroupeColonnesUtilisateur (aseMettreAJourGroupeColonnesUtilisateurParametres: ASeMettreAJourGroupeColonnesUtilisateurParametres) : Promise<ValApiResultat<any>> 
  {
    let operation: Promise<ValApiResultat<any>>;

    operation = this.ExecuterRequetePost<any> ("columns/group/update", aseMettreAJourGroupeColonnesUtilisateurParametres);

    return operation;
  }

	//Exécute une requête pour un bandeau
	//  Paramètres
	//    aseExecuterRequeteBandeauParametres : Paramètres d’éxécution d’une requête pour un bandeau
	//  Retourne
	//    Résultat de l’éxécution d’une requête pour un bandeau
  public async ExecuterRequeteBandeau (aseExecuterRequeteBandeauParametres: ASeExecuterRequeteBandeauParametres) : Promise<ValApiResultat<string[]>> 
  {
    let operation: Promise<ValApiResultat<string[]>>;
    let parametres: HttpParams;

    parametres = new HttpParams ();

    parametres = parametres.set ("Domaine", aseExecuterRequeteBandeauParametres.domaine);
    parametres = parametres.set ("IdentifiantOnglet", aseExecuterRequeteBandeauParametres.identifiantOnglet);

    operation = this.ExecuterRequeteGet<any> ("execute/bandeau", parametres);

    return operation;
  }

  //Exporte la grille vers Excel
  //  Paramètres
  //    aseExporterGrilleVersExcelParametres : Paramètres d’exportation de la grille vers Excel
  //  Retourne
  //    Résultat de l’exportation de la grille vers Excel
  public async ExporterGrilleVersExcel (aseExporterGrilleVersExcelParametres: ASeExporterGrilleVersExcelParametres) : Promise<ValApiResultat<string>> 
  {
    let operation: Promise<ValApiResultat<string>>;
    let parametres: HttpParams;

    parametres = new HttpParams ();

    parametres = parametres.set ("Domaine", aseExporterGrilleVersExcelParametres.domaine);
    parametres = parametres.set ("IdentifiantOnglet", aseExporterGrilleVersExcelParametres.identifiantOnglet);

    parametres = this.AjouterDefinitionsTrisParametres (aseExporterGrilleVersExcelParametres.definitionsTris, parametres);
    parametres = this.AjouterDefinitionsFiltresParametres (aseExporterGrilleVersExcelParametres.definitionsFiltres, parametres);

    operation = this.ExecuterRequeteGet<string> ("export/excel", parametres);

    return operation;
  }

  //Ajoute les définitions des tris aux paramètres
  //  Paramètres
  //    definitionsTris : Définitions des tris
  //  Retourne
  //    Paramètres
  private AjouterDefinitionsTrisParametres (definitionsTris: ASeDefinitionTri[], parametres: HttpParams) : HttpParams 
  {
    definitionsTris.forEach ((dtr, index) =>
    {
      parametres = parametres.set (`DefinitionsTris[${index}].Champ`, dtr.champ);
      parametres = parametres.set (`DefinitionsTris[${index}].Direction`, dtr.direction);
    });

    return parametres;
  }

  //Ajoute les définitions des filtres aux paramètres
  //  Paramètres
  //    definitionsFiltres : Définitions des filtres
  //  Retourne
  //    Paramètres
  private AjouterDefinitionsFiltresParametres (definitionsFiltres: ASeDefinitionFiltre[], parametres: HttpParams) : HttpParams 
  {
    definitionsFiltres.forEach ((dfl, index) =>
    {
      parametres = parametres.set (`DefinitionsFiltres[${index}].NomChamp`, dfl.nomChamp);
      parametres = parametres.set (`DefinitionsFiltres[${index}].Operateur`, dfl.operateur);

      if (dfl.valeurTexte !== null)
        parametres = parametres.set (`DefinitionsFiltres[${index}].ValeurTexte`, dfl.valeurTexte);

      if (dfl.valeurInt !== null)
        parametres = parametres.set (`DefinitionsFiltres[${index}].ValeurInt`, dfl.valeurInt);

      if (dfl.valeurDate !== null)
        parametres = parametres.set (`DefinitionsFiltres[${index}].ValeurDate`, this.FormaterDateFiltre (dfl.valeurDate));

      if (dfl.valeurDecimale !== null)
        parametres = parametres.set (`DefinitionsFiltres[${index}].ValeurDecimale`, dfl.valeurDecimale);

      if (dfl.valeurBoolean !== null)
        parametres = parametres.set (`DefinitionsFiltres[${index}].ValeurBoolean`, dfl.valeurBoolean);
    });

    return parametres;
  }

  //Formate la date d’un filtre : le jour choisi, sans heure ni fuseau
  //  Le sélecteur rend minuit en heure locale. toISOString l’enverrait en UTC, que l’API lie
  //  tel quel : à Paris, le 15 partirait le 14 à 23:00, et le filtre porterait sur la veille.
  //  Paramètres
  //    date : Date du filtre
  //  Retourne
  //    Date au format yyyy-MM-dd
  private FormaterDateFiltre (date: Date) : string
  {
    return `${date.getFullYear ()}-${String (date.getMonth () + 1).padStart (2, "0")}-${String (date.getDate ()).padStart (2, "0")}`;
  }
}
